import { useEffect, useMemo } from 'react';
import { Box } from '@givebutter/primitives/box';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountManagement, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { fetchAccountSession, fetchAccountSessionSecure } from 'api/account';
import { withTwoFactor } from 'assets/js/hocs/withTwoFactor';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';

const PayoutsSettings = () => {
    const { accountId } = useCurrentAccount();
    const { twoFactorMiddleware } = useFlags();
    const connectInstance = useMemo(
        () =>
            loadConnectAndInitialize({
                publishableKey: import.meta.env.VITE_DASHBOARD_STRIPE_CONNECT_KEY,
                fetchClientSecret: async () => {
                    const session = twoFactorMiddleware
                        ? await fetchAccountSessionSecure(accountId)
                        : await fetchAccountSession(accountId);
                    if (session) {
                        return session.session_token;
                    }

                    throw new Error('Failed to fetch session token');
                },
            }),
        [twoFactorMiddleware, accountId],
    );

    useEffect(() => {
        return () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.payoutAccount(accountId) });
        };
    }, [accountId]);

    return (
        <Box className="p-4">
            <ConnectComponentsProvider connectInstance={connectInstance}>
                <ConnectAccountManagement />
            </ConnectComponentsProvider>
        </Box>
    );
};

const EnhancedPayoutSettings = withTwoFactor(PayoutsSettings);

export { EnhancedPayoutSettings as PayoutsSettings };
